import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Mic, Square, Loader2, Calendar, Brain, User, CheckSquare, 
  ChevronRight, Check, Save, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Classification options
const CLASSIFICATION_OPTIONS = [
  {
    id: 'activity',
    icon: Calendar,
    title: 'Documentar Actividad',
    description: 'Reunión, llamada, email, reflexión o evento.',
    color: 'bg-blue-500',
  },
  {
    id: 'account_history',
    icon: Brain,
    title: 'Historial de Cuenta',
    description: 'Información permanente sobre la empresa.',
    color: 'bg-purple-500',
  },
  {
    id: 'relationship_history',
    icon: User,
    title: 'Historial de Relación',
    description: 'Información sobre una persona.',
    color: 'bg-green-500',
  },
  {
    id: 'tasks',
    icon: CheckSquare,
    title: 'Generar Tareas',
    description: 'Acciones concretas a realizar.',
    color: 'bg-amber-500',
  },
];

const UniversalVoiceRecorder = ({ open, onOpenChange }) => {
  const { currentUser } = useUser();
  const navigate = useNavigate();
  
  // ==================== STATE ====================
  
  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState('');
  
  // Processing
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  
  // Voice note
  const [voiceNoteId, setVoiceNoteId] = useState(null);
  
  // Flow control: recording -> classification -> complete
  const [flowStep, setFlowStep] = useState('recording');
  const [selectedClassification, setSelectedClassification] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // ==================== EFFECTS ====================
  
  useEffect(() => {
    if (open && flowStep === 'recording' && !isRecording && !audioBlob) {
      const timer = setTimeout(() => startRecording(), 300);
      return () => clearTimeout(timer);
    }
  }, [open, flowStep]);

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  // ==================== RECORDING ====================
  
  const resetState = () => {
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setAudioURL('');
    setIsSaving(false);
    setIsProcessing(false);
    setProcessingMessage('');
    setVoiceNoteId(null);
    setFlowStep('recording');
    setSelectedClassification(null);
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // ==================== SAVE AUDIO ====================
  
  const handleSaveAudio = async () => {
    if (!audioBlob) return;
    setIsSaving(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-note.webm');
      const uploadRes = await axios.post(`${API}/voice-notes/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const noteRes = await axios.post(`${API}/voice-notes`, {
        date_time: new Date().toISOString(),
        company_id: null,
        contact_id: null,
        opportunity_id: null,
        creator: currentUser?.name || null,
        audio_url: uploadRes.data.audio_url,
      }, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      setVoiceNoteId(noteRes.data.id);
      setIsSaving(false);
      setFlowStep('classification');
      toast.success('Audio guardado');
    } catch (error) {
      console.error('Error saving audio:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
      setIsSaving(false);
    }
  };

  // ==================== CLASSIFICATION ====================
  
  const handleClassificationSelect = async (classificationId) => {
    setSelectedClassification(classificationId);
    setIsProcessing(true);
    setProcessingMessage('Transcribiendo audio...');
    
    try {
      // Step 1: Transcribe
      await axios.post(`${API}/voice-notes/${voiceNoteId}/transcribe`, {}, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      // Step 2: Update classification
      setProcessingMessage('Guardando clasificación...');
      await axios.put(`${API}/voice-notes/${voiceNoteId}`, {
        classified_as: classificationId
      }, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      // Step 3: Process with AI based on category
      setProcessingMessage('Procesando con IA...');
      await axios.post(`${API}/voice-notes/${voiceNoteId}/process-category`, {}, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      setIsProcessing(false);
      setFlowStep('complete');
      toast.success('Audio procesado correctamente');
      
    } catch (error) {
      console.error('Error processing:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar. Por favor, intenta de nuevo.');
      setIsProcessing(false);
    }
  };

  // ==================== NAVIGATION ====================
  
  const handleGoToMySpace = () => {
    onOpenChange(false);
    navigate('/my-space');
  };

  // ==================== HELPERS ====================
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ==================== RENDER VIEWS ====================
  
  const renderRecordingView = () => (
    <div className="flex flex-col items-center justify-center min-h-[50vh] py-8 space-y-6">
      <div className={cn(
        "w-28 h-28 rounded-full flex items-center justify-center transition-all",
        isRecording ? "bg-red-100 animate-pulse shadow-lg shadow-red-500/30" : "bg-muted"
      )}>
        <Mic className={cn("w-14 h-14", isRecording ? "text-red-500" : "text-muted-foreground")} />
      </div>
      
      {isRecording && (
        <div className="text-4xl font-mono font-bold text-red-500">{formatTime(recordingTime)}</div>
      )}
      
      <p className="text-lg text-muted-foreground">
        {isRecording ? 'Grabando...' : 'Preparando micrófono...'}
      </p>
      
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {isRecording && (
          <Button type="button" onClick={stopRecording} variant="destructive" size="lg" className="h-14">
            <Square className="w-5 h-5 mr-2" /> Detener
          </Button>
        )}
        <Button type="button" variant="outline" size="lg" className="h-12" onClick={() => { stopRecording(); onOpenChange(false); }}>
          Cancelar
        </Button>
      </div>
      
      {audioURL && !isRecording && (
        <div className="w-full space-y-4">
          <audio src={audioURL} controls className="w-full" />
          <div className="flex flex-col gap-3">
            <Button type="button" variant="outline" onClick={() => { setAudioBlob(null); setAudioURL(''); startRecording(); }}>
              Grabar de nuevo
            </Button>
            <Button type="button" onClick={handleSaveAudio} disabled={isSaving} className="h-14">
              {isSaving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Guardando...</> : <><Save className="w-5 h-5 mr-2" /> Guardar y continuar</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderClassificationView = () => (
    <div className="py-4 space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold">¿Qué quieres hacer con este audio?</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Selecciona una opción para clasificar el audio
        </p>
      </div>
      
      {isProcessing ? (
        <div className="flex flex-col items-center py-12 space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
          <p className="text-lg font-medium">{processingMessage}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {CLASSIFICATION_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleClassificationSelect(option.id)}
                className="w-full p-4 rounded-xl border-2 text-left transition-all hover:border-primary/50 active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", option.color)}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold block">{option.title}</span>
                    <span className="text-sm text-muted-foreground">{option.description}</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCompleteView = () => {
    const option = CLASSIFICATION_OPTIONS.find(o => o.id === selectedClassification);
    const Icon = option?.icon || Check;
    
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-6">
        <div className={cn("w-20 h-20 rounded-full flex items-center justify-center", option?.color || "bg-green-500")}>
          <Icon className="w-10 h-10 text-white" />
        </div>
        
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold">¡Audio clasificado!</h3>
          <p className="text-muted-foreground">
            Tu nota de voz ha sido guardada como: <br />
            <strong>{option?.title}</strong>
          </p>
        </div>
        
        <Card className="w-full bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Para completar la documentación, ve a <strong>"Mi Espacio"</strong> y selecciona esta nota de voz para asociarla con una empresa, oportunidad o actividad.
            </p>
          </CardContent>
        </Card>
        
        <div className="flex flex-col gap-3 w-full">
          <Button type="button" onClick={handleGoToMySpace} className="h-12">
            <ExternalLink className="w-4 h-4 mr-2" />
            Ir a Mi Espacio
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full h-full sm:w-[500px] sm:max-w-lg overflow-y-auto p-4 sm:p-6"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="mb-2">
          <SheetTitle>
            {flowStep === 'recording' && 'Nueva Grabación'}
            {flowStep === 'classification' && 'Clasificar Audio'}
            {flowStep === 'complete' && 'Completado'}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {flowStep === 'recording' && 'Graba una nota de voz para procesar'}
            {flowStep === 'classification' && 'Elige cómo clasificar tu nota de voz'}
            {flowStep === 'complete' && 'Tu nota de voz ha sido procesada'}
          </SheetDescription>
        </SheetHeader>
        
        {flowStep === 'recording' && renderRecordingView()}
        {flowStep === 'classification' && renderClassificationView()}
        {flowStep === 'complete' && renderCompleteView()}
      </SheetContent>
    </Sheet>
  );
};

export default UniversalVoiceRecorder;
